/* eslint-disable no-console */
import { isRequestError } from './typeguards';
/**
 * Barebones Error object - minimum data needed to call @see logError
 */
export type BareboneLoggedError = {
  name: string;
  message: string;
  data?: { [key: string]: unknown };
};
/**
 * Either an @see Error or @see BareboneLoggedError
 */
export type LoggedError = Error | BareboneLoggedError;
/**
 * Defines available logging levels
 */
export enum LogLevel {
  None = 999,
  Info = 2,
  Warning = 3,
  Error = 4,
}
/**
 * Base log message type
 */
export type LogMessage = {
  message: string;
  source: string;
};

let globalLogLevel: LogLevel | undefined;

export const setGlobalLogLevel = (newLevel: LogLevel | undefined) => {
  globalLogLevel = newLevel;
};

/**
 * Retrieves and caches application log level
 */
const currentLogLevel = (): LogLevel => {
  if (globalLogLevel) {
    return globalLogLevel;
  }
  const logLevelString = (process.env.LOG_LEVEL || '').trim().toUpperCase();
  globalLogLevel = LogLevel.Warning;
  switch (logLevelString) {
    case 'NONE':
    case '1':
    case '0':
      globalLogLevel = LogLevel.None;
      break;
    case 'INFO':
    case '2':
      globalLogLevel = LogLevel.Info;
      break;
    case 'WARNING':
    case '3':
      globalLogLevel = LogLevel.Warning;
      break;
    case 'ERROR':
    case '4':
      globalLogLevel = LogLevel.Error;
      break;
    default:
      break;
  }
  return globalLogLevel;
};

/**
 * Determines whether checkLevel should be logged given configured error level
 * @param checkLevel Error Logging level to check against
 */
export const shouldLog = (checkLevel?: LogLevel): boolean => {
  const thisLogLevel = currentLogLevel();
  return thisLogLevel.valueOf() <= (checkLevel ?? LogLevel.Warning).valueOf();
};

/**
 * Logs an error
 * @param source Source / reason for failure.
 * @param err Error details.
 */
export const logError = (
  source: string,
  err: LoggedError | BareboneLoggedError,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info?: any
): void => {
  if (!shouldLog(LogLevel.Error)) {
    return;
  }
  // OK to log errors to console
  console.error(`An error has occurred: ${source}`, {
    ErrorDetails: err,
    AdditionalInformation: info,
  });
};

/**
 * Logs a warning
 * @param message Warning information to log
 * @param optionalParams Additional data passed to logger.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logWarning = (message: string | LogMessage, ...optionalParams: any[]): void => {
  if (!shouldLog(LogLevel.Warning)) {
    return;
  }
  let logMessage = '';
  if (typeof message === 'string') {
    logMessage = message;
  } else {
    logMessage = `${message.source}: ${message.message}`;
  }
  // OK to log warnings to console
  console.warn(logMessage, ...optionalParams);
};

/**
 * Logs a warning
 * @param message Warning information to log
 * @param optionalParams Additional data passed to logger.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logInfo = (message: string | LogMessage, ...optionalParams: any[]): void => {
  if (!shouldLog(LogLevel.Info)) {
    return;
  }
  let logMessage = '';
  if (typeof message === 'string') {
    logMessage = message;
  } else {
    logMessage = `${message.source}: ${message.message}`;
  }
  // OK to log warnings to console
  console.log(logMessage, ...optionalParams);
};

/**
 * Builds a loggable error from a caught value.
 */
export const makeLoggableError = ({
  error,
  name,
  message,
  overrideErrorMessage = false,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: Error | any;
  name?: string;
  message?: string;
  overrideErrorMessage?: boolean;
}): BareboneLoggedError => {
  const defaultErrorName = 'Unknown Error';
  if (isRequestError(error)) {
    const req = error.request();
    const url = new URL(req.url);
    url.searchParams.delete('sc_apikey');
    const res = error.response();

    const response = res
      ? {
          status: res.status,
          statusText: res.statusText,
          headers: res.headers,
          body: res.text(),
        }
      : undefined;
    return {
      ...error,
      name: name ?? defaultErrorName,
      message: overrideErrorMessage && message ? message : error.message,
      data: {
        source: error.source(),
        url: url.toString(),
        response,
      },
    };
  }
  return typeof error === 'object' && error !== null && 'message' in error
    ? {
        ...error,
        name: name ?? defaultErrorName,
        message: overrideErrorMessage && message ? message : error.message,
      }
    : {
        name: name ?? defaultErrorName,
        message: message ?? 'Unexpected error occurred',
        data: error,
      };
};
